import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/app-shell';
import { AppointmentDetailPage } from '@/pages/appointment-detail-page';
import { AppointmentsPage } from '@/pages/appointments-page';
import { BookingPage } from '@/pages/booking-page';
import { HistoryPage } from '@/pages/history-page';
import { HomePage } from '@/pages/home-page';
import { LoginPage } from '@/pages/login-page';
import { PlanPage } from '@/pages/plan-page';
import { ProfilePage } from '@/pages/profile-page';
import {
  ApiError,
  Button,
  Card,
  ErrorState,
  LoadingState,
  TenantProvider,
  createQueryClient,
  hostIdentifiesTenant,
  resolveTenantSlug,
  useAuthStore,
  useTenantQuery,
} from '@barber/ui';

const queryClient = createQueryClient();

/**
 * A raiz.
 *
 * ---
 *
 * A ORDEM DO BOOT NÃO É ARBITRÁRIA
 *
 *   1. descobrir a BARBEARIA  → sem ela não há cores, nome nem regras;
 *   2. restaurar a SESSÃO     → o cookie de refresh dirá se já há alguém logado;
 *   3. só então rotear.
 *
 * Inverter 1 e 2 mostraria a tela de login sem identidade — o cliente veria uma
 * página cinza pedindo o telefone dele, o que é exatamente o que um golpe
 * pareceria.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Boot />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function Boot() {
  const tenant = useTenantQuery();
  const status = useAuthStore((s) => s.status);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (tenant.isPending || status === 'checking') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <LoadingState label="Carregando…" />
      </div>
    );
  }

  if (tenant.isError) return <TenantNaoResolvida erro={tenant.error} />;

  return (
    <TenantProvider tenant={tenant.data}>
      <Rotas />
    </TenantProvider>
  );
}

function Rotas() {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/entrar" element={<LoginPage />} />
        {/*
          Guarda o destino: quem tocou no link de um horário específico volta
          para ELE depois de entrar, e não para a home.
        */}
        <Route
          path="*"
          element={<Navigate to="/entrar" replace state={{ from: location.pathname }} />}
        />
      </Routes>
    );
  }

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/agendar" element={<BookingPage />} />
        <Route path="/horarios" element={<AppointmentsPage />} />
        <Route path="/horarios/:id" element={<AppointmentDetailPage />} />
        <Route path="/plano" element={<PlanPage />} />
        <Route path="/historico" element={<HistoryPage />} />
        <Route path="/perfil" element={<ProfilePage />} />
        {/* Já autenticado, a tela de entrar não tem o que fazer. */}
        <Route path="/entrar" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NaoEncontrada />} />
      </Routes>
    </AppShell>
  );
}

/**
 * O app não sabe de qual barbearia é.
 *
 * Em produção isso é um endereço errado. Em desenvolvimento, quase sempre é a
 * falta do slug — e aí a tela oferece o caminho em vez de só reclamar.
 */
function TenantNaoResolvida({ erro }: { erro: unknown }) {
  const semSlug = !hostIdentifiesTenant() && !resolveTenantSlug();
  const offline = erro instanceof ApiError && erro.isOffline;

  if (semSlug) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-4 px-5">
        <Card className="flex flex-col gap-3">
          <h1 className="text-lg font-bold text-content">Qual barbearia?</h1>
          <p className="text-sm text-content-muted">
            Em produção, o endereço identifica a barbearia. Aqui em{' '}
            <code className="rounded bg-surface-muted px-1">localhost</code> não há subdomínio,
            então abra pelo caminho <code className="rounded bg-surface-muted px-1">/b/:slug</code>{' '}
            ou defina <code className="rounded bg-surface-muted px-1">VITE_TENANT_SLUG</code>.
          </p>

          <div className="flex flex-col gap-2">
            {['barbearia-alfa', 'studio-beta'].map((slug) => (
              <Button
                key={slug}
                variant="secondary"
                onClick={() => {
                  window.location.href = `/b/${slug}`;
                }}
              >
                Abrir /b/{slug}
              </Button>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center px-5">
      <ErrorState
        title={offline ? 'Sem conexão' : 'Barbearia não encontrada'}
        description={
          offline
            ? 'Verifique sua internet e tente de novo.'
            : 'Confira o endereço. Se você chegou por um link, peça um novo à barbearia.'
        }
        onRetry={() => window.location.reload()}
      />
    </div>
  );
}

function NaoEncontrada() {
  return (
    <ErrorState
      title="Página não encontrada"
      description="O endereço não existe ou mudou."
      onRetry={() => {
        window.location.href = '/';
      }}
    />
  );
}
